'use client';

/**
 * ProductTile — the shared product-tile primitive.
 *
 * Extracted verbatim from vendor catalog's inline `ProductCard` so the
 * browse grid and the search-results grid paint the SAME tile (image,
 * name, price, add-to-RFQ, in-cart state). "Cards are meant to be
 * borrowed" — one tile, two feeders (vendor_explore browse, product_search
 * results). The caller supplies the per-instance CardPlacementProvider.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useState, useCallback, useMemo } from 'react';
import { Package, ShoppingCart, Check, Heart, PackagePlus, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useCardExport } from '@/lib/design/card-export';
import { useCartStore } from '@/lib/stores/cart-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useAffordance, useCostPlanLabel } from '@/lib/stores/total-matrix-store';
import { usePinnedCostPlanStore } from '@/lib/stores/pinned-costplan-store';
import { useFocusedRequirementStore } from '@/lib/stores/focused-requirement-store';
import { useSaveToggle, useMcpLineAppend } from '@/lib/api/hooks';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';
import { testIds } from '@/lib/test-utils/selectors';
import type { ProductSummary } from '@/lib/api/types';

export function ProductTile({
  product,
  vendorId,
  vendorName,
  showVendor = false,
  queryKey,
  collectionName,
  onClick,
  isSelected = false,
  badge,
  meta,
  matchText,
  priceText,
  subtitle,
  sourceLineSid,
  sourceLineLabel,
  addToCartTestIdOverride,
  isPicked = false,
  pickedPlanNames,
}: {
  product: ProductSummary;
  vendorId: string;
  vendorName: string;
  /** Paint the vendor line — on for cross-vendor grids (search); off for a
   *  single-vendor browse where it would just repeat. */
  showVendor?: boolean;
  /** null for store-driven feeders (intent film-roll) — scene-only export. */
  queryKey: readonly unknown[] | null;
  collectionName: string;
  onClick: () => void;
  /** This tile is the one currently inspected (driving the Brief). Draws a
   *  primary ring so the canvas shows what the rail is describing. */
  isSelected?: boolean;
  // ── Optional VERDICT layer (mode3 intent film-roll) — painted strings
  // from the caller; the tile stays dumb. Tier expresses as a badge,
  // never the border: elevation owns the container (settled design). ──
  badge?: { label: string; className: string };
  /** Attribute line under the name ("occasional chair · leather · brown"). */
  meta?: string;
  /** Match % — right-aligned on the price row. */
  matchText?: string;
  /** Painted price override ("Price on request"); default = list_price. */
  priceText?: string;
  /** Italic footnote (refinement narrative). */
  subtitle?: string;
  /** The schedule swimlane this tile belongs to (mode3 intent grid only) —
   *  carried onto the cart line so several vendor picks for the SAME
   *  schedule line stay distinct rows instead of merging. undefined for
   *  plain catalogue browsing (no schedule context). */
  sourceLineSid?: string | null;
  sourceLineLabel?: string | null;
  /** Alternate, caller-supplied test-id for the add-to-cart button —
   *  used by the mode3 intent swimlane to address this exact rendered
   *  instance by (line_id, position) instead of product identity (see
   *  canvas.intent_swimlane.pick_at_position). Falls back to the
   *  tile's own identity-keyed default when omitted (vendor browse,
   *  product search — no swimlane/position context). */
  addToCartTestIdOverride?: string;
  /** Already a real pick on this schedule line (docs/mcp_refined_ux.md
   *  §3) — mode3 intent film-roll only, via `IntentGridProduct.is_picked`.
   *  Not the ephemeral cart-store `isInCart`/`justAdded` state below:
   *  this is a durable graph fact that may be weeks old, so it gets its
   *  own badge rather than reusing ShoppingCart/Check. */
  isPicked?: boolean;
  /** Which CostPlan(s) picked it — for the badge's hover text. More
   *  than one only when the request was unscoped (no plan pinned). */
  pickedPlanNames?: string[];
}) {
  const [justAdded, setJustAdded] = useState(false);
  const addItem = useCartStore((s) => s.addItem);
  // Dynamic — every tile in a grid mounts simultaneously, so this needs
  // the product's own id, not a static key. Replaces the legacy
  // testIds.vendorCatalog.addToCart(...) hand-typed derived string.
  const defaultAddToCartTestId = useAffordance('canvas.product_tile.add_to_cart', { id: product.semantic_id });
  const addToCartTestId = addToCartTestIdOverride ?? defaultAddToCartTestId;
  const items = useCartStore((s) => s.items);
  const isInCart = items.some(
    (i) => i.product_sid === product.semantic_id
      && (i.source_line_sid ?? null) === (sourceLineSid ?? null),
  );

  // ★ favourite — server truth (product.saved, from the SAVED edge) with an optimistic
  // override for the in-flight toggle. The client owns the desired state (no read-then-write).
  const [savedOverride, setSavedOverride] = useState<boolean | null>(null);
  const saved = savedOverride ?? product.saved ?? false;
  const saveStrategy = useCapabilityStore((s) => s.strategyName('products', 'save_product'));
  const saveToggle = useSaveToggle(saveStrategy ?? '');

  // ── Add-to-focused-requirement — the Cost Plan rail surface's
  // affordance (mode3/mode11 only). Separate from the cart's
  // add-to-RFQ button above: this appends straight onto the PINNED
  // cost plan's focused line, bypassing the cart-staging path. Only
  // unambiguous when both a plan is pinned AND a requirement is
  // focused — otherwise there's no single target to append to. ──
  const pinnedMcpId = usePinnedCostPlanStore((s) => s.pinnedMcpId);
  const focusedSourceLineSid = useFocusedRequirementStore((s) => s.focusedSourceLineSid);
  const focusedSourceLineLabel = useFocusedRequirementStore((s) => s.focusedSourceLineLabel);
  const lineAppendStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'line_append'));
  const lineAppend = useMcpLineAppend(lineAppendStrategy ?? '');
  const addToLineLabel = useCostPlanLabel('add_to_line_action');
  const addToLineToast = useCostPlanLabel('add_to_line_toast', { line_label: focusedSourceLineLabel ?? '' });
  const alreadyPickedForLineLabel = useCostPlanLabel('already_picked_for_line_action');
  const canAddToLine = !!pinnedMcpId && !!focusedSourceLineSid;

  // Product-tile affordance labels (chrome lanes, Pattern A) — shared
  // across every surface this tile mounts on, kept in cost_plan.py
  // alongside add_to_line_action per that file's own "product tile
  // affordance" section.
  const addToRfqLabel = useCostPlanLabel('add_to_rfq_action');
  const addMoreToRfqLabel = useCostPlanLabel('add_more_to_rfq_action');
  const saveLabel = useCostPlanLabel('save_action');
  const savedLabel = useCostPlanLabel('saved_action');
  const pickedBadgeTooltip = useCostPlanLabel('picked_badge_tooltip', {
    plans: pickedPlanNames?.join(', ') ?? '',
  });

  const handleAddToLine = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!pinnedMcpId || !focusedSourceLineSid || !lineAppendStrategy) return;
      lineAppend.mutate(
        {
          semantic_id: pinnedMcpId,
          line: {
            product_sid: product.semantic_id,
            product_name: product.name,
            vendor_id: vendorId,
            vendor_name: vendorName,
            quantity: 1,
            unit_cost: product.list_price,
            image_url: product.image_url,
            source_line_sid: focusedSourceLineSid,
          },
        },
        { onSuccess: () => toast(addToLineToast) },
      );
    },
    [pinnedMcpId, focusedSourceLineSid, lineAppendStrategy, lineAppend, product, vendorId, vendorName, addToLineToast],
  );

  // Scene export — this product as a card. in_cart / just_added are
  // screen-truth that lives nowhere in the source payload; a leaf tree
  // would drop them. price ships PAINTED ("$1,234") to match the tile.
  // surface + slot_id come from the parent provider.
  const priceLabel = priceText
    ?? (product.list_price != null ? `$${product.list_price.toLocaleString()}` : null);
  useCardExport(useMemo(() => ({
    label: product.name,
    content_key: 'product',
    status: 'loaded' as const,
    query_key: queryKey,
    text: `${product.name}${vendorName ? ` · ${vendorName}` : ''}${priceLabel ? ` — ${priceLabel}` : ''}${badge ? ` [${badge.label}${matchText ? ` · ${matchText}` : ''}]` : ''}`,
    json: {
      product_id: product.semantic_id,
      name: product.name,
      vendor: vendorName || null,
      price: priceLabel,
      image_url: product.image_url ?? null,
      collection: collectionName,
      // Verdict layer (present only when the feeder adorns) — painted.
      tier: badge?.label ?? null,
      match: matchText ?? null,
      meta: meta ?? null,
    },
    state: { in_cart: isInCart, just_added: justAdded, selected: isSelected, saved },
  }), [product, vendorName, priceLabel, queryKey, collectionName, isInCart, justAdded, isSelected, saved, badge, matchText, meta]));

  const handleAddToCart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick();
      addItem({
        product_sid: product.semantic_id,
        name: product.name,
        quantity: 1,
        vendor_id: vendorId,
        vendor_name: vendorName,
        list_price: product.list_price,
        image_url: product.image_url,
        source_line_sid: sourceLineSid,
        source_line_label: sourceLineLabel,
      });
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1500);
    },
    [addItem, product, vendorId, vendorName, onClick, sourceLineSid, sourceLineLabel],
  );

  const handleToggleSave = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!saveStrategy) return;
      const next = !saved;
      setSavedOverride(next);  // optimistic fill
      saveToggle.mutate(
        { product_sid: product.semantic_id, saved: next },
        { onError: () => setSavedOverride(!next) },  // revert on failure
      );
    },
    [saved, saveStrategy, saveToggle, product.semantic_id],
  );

  return (
    <div
      data-testid={testIds.vendorCatalog.productCard(product.semantic_id)}
      onClick={onClick}
      className={cn(
        'group relative rounded-lg border border-border overflow-hidden cursor-pointer',
        'bg-card shadow-elev-1 hover:border-primary/50 hover:shadow-elev-2 transition-all text-left flex flex-col',
        isSelected && 'border-primary ring-2 ring-primary ring-offset-1 ring-offset-background shadow-elev-2',
      )}
    >
      <div className="aspect-[4/3] bg-muted flex items-center justify-center overflow-hidden relative">
        {product.image_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Package className="w-8 h-8 text-muted-foreground opacity-40" />
        )}

        {badge && (
          <span
            className={cn(
              'absolute top-2 left-2 text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide',
              badge.className,
            )}
          >
            {badge.label}
          </span>
        )}
        {isPicked && (
          <span
            className="absolute top-2 right-2 flex items-center justify-center w-5 h-5 rounded-full bg-foreground/85 text-background shadow-elev-1"
            title={pickedBadgeTooltip}
          >
            <BadgeCheck className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
      <div className="p-1.5 space-y-0.5">
        {/* Full name on hover — the clamp hides exactly the variant
            differentiator on colourway families (Aruba "… - Misty Grey"
            vs "… - Sand Dune" read as duplicates without it). */}
        <h3
          title={product.name}
          className={cn(text.title, 'line-clamp-2 leading-tight min-h-[2.5em] group-hover:text-primary transition-colors')}
        >
          {product.name}
        </h3>
        {meta && (
          <p className={cn(text.muted, 'truncate')} title={meta}>{meta}</p>
        )}
        {showVendor && vendorName && (
          <p className={cn(text.muted, 'truncate')}>{vendorName}</p>
        )}
        {/* Footer — price (left) + affordances (right), at the card's
            bottom-right next to the price. Always rendered so the ♥/cart have a
            home even when the tile has no price. Hover-reveal at rest; a saved ♥
            or in-cart chip stays lit. Icons stopPropagation, so a tap acts, not
            selects. */}
        <div className="flex items-center justify-between gap-1 pt-0.5">
          <span className={cn(text.muted, 'min-w-0 flex items-baseline gap-1.5')}>
            {priceLabel && <span className="truncate">{priceLabel}</span>}
            {matchText && <span className="tabular-nums shrink-0">{matchText}</span>}
          </span>
          <div className="flex items-center gap-0.5 shrink-0 -mr-0.5">
            <button
              onClick={handleToggleSave}
              disabled={!saveStrategy}
              className={cn(
                'p-1 rounded-md transition-all',
                saved
                  ? 'opacity-100 text-rose-400'
                  : 'opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-rose-400',
              )}
              title={saved ? savedLabel : saveLabel}
            >
              <Heart className={cn('w-4 h-4', saved && 'fill-current')} />
            </button>
            <button
              data-testid={addToCartTestId}
              onClick={handleAddToCart}
              className={cn(
                'p-1 rounded-md transition-all',
                justAdded
                  ? 'opacity-100 text-green-600'
                  : isInCart
                    ? 'opacity-100 text-primary'
                    : 'opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary',
              )}
              title={isInCart ? addMoreToRfqLabel : addToRfqLabel}
            >
              {justAdded ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
            </button>
            {canAddToLine && (
              <button
                onClick={handleAddToLine}
                disabled={!lineAppendStrategy || lineAppend.isPending || isPicked}
                className={cn(
                  'p-1 rounded-md transition-all text-muted-foreground hover:text-primary disabled:opacity-50',
                  isPicked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                )}
                title={isPicked ? alreadyPickedForLineLabel : addToLineLabel}
              >
                <PackagePlus className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {subtitle && (
          <p className={cn(text.muted, 'italic truncate')} title={subtitle}>{subtitle}</p>
        )}
      </div>
    </div>
  );
}
